import React, { useContext, useEffect, useState } from 'react';
import { TeckStackStep } from './TeckStackStep';
import { ScheduleMeetingStep } from './ScheduleMeetingStep';
import { DesktopMeetingScheduleBody } from './DesktopMeetingScheduleBody';
import { TopTierCompanies } from './TopTierCompanies';
import { DayTimeSlot, GetMeetingsAPIResponse, MeetingStatus, MeetingType, SlotTime, UIUserAvailability } from 'types';
import { Drawer } from '../shared/Drawer';
import moment from 'moment';
import { useGetAvailabilitiesForScheduling } from 'src/hooks/userAvailability.hooks';
import { useMutation, useQueryClient } from 'react-query';
import { useToast } from 'src/context/ToastProvider';
import { useUserContext } from 'src/context/UserProvider';
import { rescheduleMeeting, scheduleMeeting } from 'src/apis/meeting';
import { findRemainingAttempts, isUserCollegeUser } from 'shared-utils';
import { useGetAllSellableRoundTemplates } from 'src/hooks/roundTemplate.hooks';
import CompletePhoneVerificationModal from './CompletePhoneVerificationModal';
import { useLocation, useNavigate } from 'react-router-dom';
import { get } from 'lodash';
import { useGetPaymentStatusForInterviewOrPrepQuery } from 'src/hooks/payment.hooks';
import Container from '../shared/Container';
import { useMediaQuery } from '@mui/material';
import { ThemeContext } from 'src/context/ThemeProvider';
import ShowTemplateLockModal from './ShowTemplateLockModal';

interface Props {
	isOpen: boolean;
	onClose: () => void;
	showMockTestOnly?: boolean;
	setShowMockTestOnly?: React.Dispatch<React.SetStateAction<boolean>>;
	interviewType?: MeetingType | null;
	setInterviewType?: React.Dispatch<React.SetStateAction<MeetingType | null>>;
}

export const ScheduleInterviewDrawer = ({
	isOpen,
	onClose,
	showMockTestOnly,
	setShowMockTestOnly,
	interviewType,
	setInterviewType,
}: Props) => {
	const queryClient = useQueryClient();
	const isMobile = useMediaQuery('(max-width: 960px)');
	const toast = useToast();
	const {
		user,
		hasReschedulableMeeting,
		userRounds,
		latestScheduledMeeting,
		allowUserToScheduleMeeting,
		hasAvailedAllFreeInterviews,
		isJobseekerLoggedIn,
		showAwaitingResults,
	} = useUserContext();

	const [isCompletePhoneVerificationModal, setIsCompletePhoneVerificationModal] = useState<boolean>(false);

	const location = useLocation();
	const navigate = useNavigate();
	const [allSlots, setAllSlots] = useState<DayTimeSlot[]>([]);
	const [selectedTeckStack, setSelectedTeckStack] = useState<string>(); // round TemplateIds
	const [openTemplateLockModal, setOpenTemplateLockModal] = useState(false);

	const [selectedRound, setSelectedRound] = useState<string>(); // round ids
	const [selectedDaySlot, setSelectedDaySlot] = useState<DayTimeSlot>();
	const [selectedTimeSlot, setSelectedTimeSlot] = useState<SlotTime>();
	const [currentStep, setCurrentStep] = useState<number>(1);
	const [isAIRoundSelected, setIsAIRoundSelected] = useState<boolean>(false);
	const ThemeContextState = useContext(ThemeContext);

	const { data: roundTemplateData } = useGetAllSellableRoundTemplates(isJobseekerLoggedIn, !selectedRound);

	const { data, isLoading, refetch } = useGetAvailabilitiesForScheduling({
		round: selectedRound,
		roundTemplateId: selectedTeckStack,
		isAIRound: isAIRoundSelected,
	});

	const { data: interviewBookingPayment } = useGetPaymentStatusForInterviewOrPrepQuery(isJobseekerLoggedIn, true);

	const scheduleMeetingForRoundMutationFn = useMutation(
		[],
		async () =>
			hasReschedulableMeeting && latestScheduledMeeting
				? await rescheduleMeeting({
						meetingId: latestScheduledMeeting?._id,
						startTime: moment(selectedTimeSlot?.time).toDate(),
				  })
				: await scheduleMeeting({
						meetingStartTime: moment(selectedTimeSlot?.time).toDate(),
						roundTemplateId: selectedTeckStack,
						roundId: selectedRound,
				  }),

		{
			onSuccess() {
				queryClient.refetchQueries(['verifyCookie']);
				queryClient.refetchQueries(['getMeetings']).then(async e => {
					const data: GetMeetingsAPIResponse | undefined = await queryClient.getQueryData(['getMeetings']);
					if (data && location.pathname !== '/prepare') {
						const meeting =
							data?.meetings?.length &&
							data?.meetings?.filter(m => [MeetingStatus.SCHEDULED, MeetingStatus.STARTED].includes(m.meetingStatus))
								? data?.meetings.filter(m =>
										[MeetingStatus.SCHEDULED, MeetingStatus.STARTED].includes(m.meetingStatus),
								  )[0]
								: null;
						if (meeting) {
							navigate(`/interviews/live/${meeting?._id}`);
						}
					}
				});

				toast({
					title: 'Meeting Scheduled',
				});
				if (location.pathname === '/prepare') navigate('/interviews');
				// sendTrackingEvent({
				// 	event: 'interview_pop_up_disc._agree',
				// 	eventLabel: router.asPath,
				// });
				setOpenTemplateLockModal(false);
				handleClose();
			},
			onError(error: Error) {
				const errorMessage = get(error, ['response', 'data', 'message'], error.message);
				toast({
					type: 'error',
					title: errorMessage,
				});

				handleClose();
			},
		},
	);

	const handleClose = () => {
		// onComplete && onComplete();
		setCurrentStep(1);
		onClose();
	};

	const handleScheduleInterviewForPaidUser = () => {
		if (!user?.phone || !user.flags.isPhoneVerified) {
			handleClose();
			setIsCompletePhoneVerificationModal(true);

			onClose();
		} else {
			scheduleMeetingForRoundMutationFn.mutate();
		}
	};

	useEffect(() => {
		setSelectedRound(
			userRounds
				.filter(
					r =>
						(showMockTestOnly ? r.isMock : !r?.isMock) &&
						(!interviewType ||
							(interviewType === MeetingType.Assessment
								? interviewType === r.meetingType
								: r.meetingType !== MeetingType.Assessment)),
				)
				.find(r => findRemainingAttempts(r.maxAttempts, r.meetings))?._id ?? '',
		);
	}, [userRounds]);

	useEffect(() => {
		if (data?.slots && !isAIRoundSelected) {
			const userAvailabilities = data.slots as UIUserAvailability[];

			if (!userAvailabilities.length) {
				console.log('from here');
				setAllSlots([]);
				setSelectedDaySlot(undefined);
				setSelectedTimeSlot(undefined);
				return;
			}

			const dayWiseSlots = userAvailabilities
				.sort((avail1, avail2) => moment(avail1.startTime).valueOf() - moment(avail2.startTime).valueOf())
				.reduce((acc: Record<string, DayTimeSlot>, userAvailability) => {
					const formattedDate = moment(userAvailability.startTime).format('DD MMM');
					if (!acc[formattedDate]) {
						acc[formattedDate] = {
							day: userAvailability.day,
							date: userAvailability.startTime,
							duration: userAvailability.duration,
							slotTimes: [{ time: userAvailability.startTime, count: 1 }],
							totalSlots: 1,
						};
					} else {
						const dayObject = acc[formattedDate];
						const slotTimeIndex = dayObject.slotTimes.findIndex(
							(time: SlotTime) => moment(time.time).valueOf() === moment(userAvailability.startTime).valueOf(),
						);
						if (slotTimeIndex < 0) {
							acc[formattedDate] = {
								...dayObject,
								slotTimes: [...dayObject.slotTimes, { time: userAvailability.startTime, count: 1 }],
								totalSlots: dayObject.totalSlots + 1,
							};
						}
					}
					return acc;
				}, {});

			let availableSlots = Object.values(dayWiseSlots)
				.filter(ds => ds.slotTimes.length > 0)
				.map(daySlot => ({
					...daySlot,
					slotTimes: daySlot.slotTimes.sort(
						(slot1, slot2) => moment(slot1.time).valueOf() - moment(slot2.time).valueOf(),
					),
				}));

			const firstAvailableDay = availableSlots.find(ds => ds.slotTimes.length > 0);
			const firstAvailableSlot = firstAvailableDay?.slotTimes[0];

			setAllSlots(availableSlots);
			setSelectedDaySlot(firstAvailableDay);
			setSelectedTimeSlot(firstAvailableSlot);
		}
	}, [data?.slots, isAIRoundSelected]);

	useEffect(() => {
		const firstAvailableSlot = selectedDaySlot?.slotTimes[0];
		firstAvailableSlot && setSelectedTimeSlot(firstAvailableSlot);
	}, [selectedDaySlot]);

	useEffect(() => {
		if (isOpen) refetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedTeckStack, isOpen, selectedRound]);

	useEffect(() => {
		if (roundTemplateData?.roundTemplates?.length) {
			const defaultRoundTemplate = roundTemplateData.roundTemplates?.find(
				el =>
					(showMockTestOnly ? el.isMock : !el?.isMock) &&
					(!interviewType ||
						(interviewType === MeetingType.Assessment
							? interviewType === el.templateType
							: el.templateType !== MeetingType.Assessment)),
			);

			setSelectedTeckStack(defaultRoundTemplate?._id);
			setIsAIRoundSelected(
				defaultRoundTemplate?.isAIInterviewTemplate || defaultRoundTemplate?.templateType === MeetingType.Assessment,
			);
		} else {
			setSelectedTeckStack('');
			setIsAIRoundSelected(false);
		}
	}, [roundTemplateData, isOpen, showMockTestOnly]);

	useEffect(() => {
		if (hasReschedulableMeeting && latestScheduledMeeting) {
			setSelectedRound(String(latestScheduledMeeting.round));
			setIsAIRoundSelected(latestScheduledMeeting.isAIInterview || false);
		}
	}, [hasReschedulableMeeting, latestScheduledMeeting]);

	if (
		!allowUserToScheduleMeeting &&
		!hasReschedulableMeeting &&
		hasAvailedAllFreeInterviews &&
		!user?.flags.showKyl &&
		!showAwaitingResults
	)
		return null;

	const hasAlreadyPaidForInterview =
		(interviewBookingPayment?.hasPaid && Number(interviewBookingPayment.amount) > 0) ?? false;

	return (
		<div>
			<Drawer
				isOpen={isOpen}
				onClose={handleClose}
				backgroundColor={isMobile ? '' : ThemeContextState.themeMode === 'light' ? '#fff' : '#111E2C'}
				minHeight="fit-content"
				header={
					showMockTestOnly || (user && !isUserCollegeUser(user)) ? (
						<div className=" md:hidden font-inter w-full text-[18px] pl-4 mt-4 mb-2 max-w-[280px]">
							{hasReschedulableMeeting ? 'Reschedule' : 'Schedule'} your{' '}
							{hasReschedulableMeeting ? (
								<span className="greenish-gradient-text"> {latestScheduledMeeting?.title}</span>
							) : (
								' round'
							)}
							{` of `}
							{latestScheduledMeeting?.duration ??
								userRounds.find(ur => String(ur._id) === selectedRound)?.roundTemplate.duration ??
								roundTemplateData?.roundTemplates?.find(rT => String(rT._id) === selectedTeckStack)?.duration ??
								0}{' '}
							mins
						</div>
					) : (
						<div className="md:hidden font-inter font-[500] text-[#1F3343] dark:text-[#FFFFFF] leading-[28px] text-[18px] pl-4 mt-4 mb-2 max-w-[320px]">
							Start your <strong>Skills assessment</strong>
						</div>
					)
				}
				body={
					<Container className="w-full !px-0 xl:!px-[80px]">
						<div className="md:hidden block">
							{currentStep === 1 && !hasReschedulableMeeting ? (
								<TeckStackStep
									interviewType={interviewType}
									setInterviewType={setInterviewType}
									showMockTestOnly={showMockTestOnly}
									setShowMockTestOnly={setShowMockTestOnly}
									selectedRound={selectedRound ?? ''}
									setSelectedRound={setSelectedRound}
									setIsAIRoundSelected={setIsAIRoundSelected}
									roundTemplates={roundTemplateData?.roundTemplates ?? []}
									setSelectedTechStack={setSelectedTeckStack}
									selectedTeckStack={selectedTeckStack ?? ''}
									currentStep={currentStep}
									setCurrentStep={setCurrentStep}
									onNext={() => {
										if (user && !user?.roundTemplateLabel && isUserCollegeUser(user) && !showMockTestOnly) {
											setOpenTemplateLockModal(true);
										} else {
											setCurrentStep(2);
										}
									}}
								/>
							) : (
								<ScheduleMeetingStep
									allSlots={allSlots}
									selectedDaySlot={selectedDaySlot}
									selectedTimeSlot={selectedTimeSlot}
									setSelectedDaySlot={setSelectedDaySlot}
									setSelectedTimeSlot={setSelectedTimeSlot}
									selectedTeckStack={selectedTeckStack ?? ''}
									isAIRoundSelected={isAIRoundSelected}
									currentStep={currentStep}
									setCurrentStep={setCurrentStep}
									onSchedule={() => {
										if (!user?.phone || !user.flags.isPhoneVerified) {
											setIsCompletePhoneVerificationModal(true);
											onClose();
										} else {
											setIsCompletePhoneVerificationModal(false);
											scheduleMeetingForRoundMutationFn.mutate();
										}
									}}
									onClose={onClose}
									isLoading={scheduleMeetingForRoundMutationFn.isLoading}
									handleScheduleInterviewForPaidUser={handleScheduleInterviewForPaidUser}
									hasAlreadyPaidForInterview={hasAlreadyPaidForInterview}
								/>
							)}
						</div>
						<div className="md:block hidden">
							<DesktopMeetingScheduleBody
								isOpen={isOpen}
								fetchingSlots={isLoading}
								interviewType={interviewType}
								setInterviewType={setInterviewType}
								showMockTestOnly={showMockTestOnly}
								setShowMockTestOnly={setShowMockTestOnly}
								isLoading={scheduleMeetingForRoundMutationFn.isLoading}
								allSlots={allSlots}
								onSchedule={() => {
									if (!user?.phone || !user.flags.isPhoneVerified) {
										setIsCompletePhoneVerificationModal(true);
										onClose();
									} else if (user && !user?.roundTemplateLabel && isUserCollegeUser(user) && !showMockTestOnly) {
										setOpenTemplateLockModal(true);
									} else {
										setIsCompletePhoneVerificationModal(false);
										scheduleMeetingForRoundMutationFn.mutate();
									}
								}}
								selectedDaySlot={selectedDaySlot}
								selectedTimeSlot={selectedTimeSlot}
								setSelectedDaySlot={setSelectedDaySlot}
								setSelectedTimeSlot={setSelectedTimeSlot}
								roundTemplates={roundTemplateData?.roundTemplates ?? []}
								selectedTeckStack={selectedTeckStack ?? ''}
								selectedRound={selectedRound ?? ''}
								isAIRoundSelected={isAIRoundSelected}
								setCurrentStep={setCurrentStep}
								onClose={onClose}
								setSelectedTechStack={setSelectedTeckStack}
								setIsAIRoundSelected={setIsAIRoundSelected}
								setSelectedRound={setSelectedRound}
								handleScheduleInterviewForPaidUser={handleScheduleInterviewForPaidUser}
								hasAlreadyPaidForInterview={hasAlreadyPaidForInterview}
							/>
						</div>
					</Container>
				}
				footer={
					isAIRoundSelected ? null : (
						<div
							className="p-[12px] md:hidden"
							style={{
								boxShadow: 'inset 0px 5px 5px -5px rgba(0, 0, 0, 1)',
							}}
						>
							<TopTierCompanies />
						</div>
					)
				}
			/>

			<CompletePhoneVerificationModal
				isOpen={isCompletePhoneVerificationModal}
				onClose={() => setIsCompletePhoneVerificationModal(false)}
				onCompleteScheduling={() => scheduleMeetingForRoundMutationFn.mutate()}
			/>

			<ShowTemplateLockModal
				isAIRoundSelected={isAIRoundSelected}
				isLoading={scheduleMeetingForRoundMutationFn?.isLoading}
				selectedRoundTemplate={
					selectedRound
						? userRounds?.find(el => el?._id?.toString() === selectedRound?.toString())?.title
						: selectedTeckStack
						  ? roundTemplateData?.roundTemplates?.find(el => el?._id?.toString() === selectedTeckStack?.toString())
									?.title
						  : null
				}
				templateLabel={
					selectedRound
						? userRounds
								?.find(el => el?._id?.toString() === selectedRound?.toString())
								?.roundTemplate?.label?.toString()
						: selectedTeckStack
						  ? roundTemplateData?.roundTemplates
									?.find(el => el?._id?.toString() === selectedTeckStack?.toString())
									?.label?._id?.toString()
						  : null
				}
				isOpen={openTemplateLockModal}
				onClose={() => setOpenTemplateLockModal(false)}
				onConfirm={() => {
					if (!isMobile) scheduleMeetingForRoundMutationFn.mutate();
					else {
						setCurrentStep(2);
						setOpenTemplateLockModal(false);
					}
				}}
			/>
		</div>
	);
};
